import React, { useState, useRef, useCallback } from 'react';
import { Loader } from 'lucide-react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FileType, FolderType } from '../../../../types';
import { pushModal } from '../../../globalModals/globalModalsSlice';
import FileActionMenu from '../FileActionMenu';
import { RootState } from '../../../../reducers';
import DefaultThumbnail from '../../../../assets/recording/WhiteThumbnail.svg';
import { useCachedThumbnail } from '../../../../hooks/useCachedThumbnail';
import { Checkbox } from '../../../../components/ui/checkbox';
import { TableCell, TableRow } from '../../../../components/ui/table';
import { Skeleton } from '../../../../components/ui/skeleton';
import { getCachedImportUrl } from '../../../../middleware/socketMiddleware/handleUpdate';
import { formatDuration } from '../../../../lib/videoUtils';
import { FileListItemShell } from '../components/FileListItemShell';
import { FolderSelector } from '../components/FolderSelector';
import { selectVideoFromLibrary } from '../../../selectedVideo/selectedVideoSlice';

// Preload the SelectedVideo component (debounced at module level)
let preloadPromise: Promise<any> | null = null;
const preloadSelectedVideo = () => {
  if (!preloadPromise) {
    preloadPromise = import('../../../selectedVideo');
  }
  return preloadPromise;
};

interface FileListItemProps {
  id?: string;
  file: FileType;
  isSelected: boolean;
  selectMode?: boolean;
  onSelect?: (file: FileType) => void;
  fileUrl: string;
  isMobile?: boolean;
}

// Create motion-enabled TableRow component
const MotionTableRow = motion(TableRow);

// Minimalist import processing row - matches grid view style
const ProcessingListItem = ({ name, url }: { name: string; url?: string }) => {
  return (
    <FileListItemShell name={name} youtubeUrl={url} statusText="Importing..." />
  );
};

const FileListItem = React.memo(
  ({
    file,
    fileUrl,
    isSelected,
    selectMode,
    onSelect,
    isMobile = false,
  }: FileListItemProps) => {
    const {
      name,
      thumbUrl,
      createdAt,
      owner,
      duration,
      parentId,
      type,
      _id,
      url,
    } = file;
    const { name: ownerName } = owner || { name: '' };
    const [showMenu, setShowMenu] = useState(false);
    const [isHovering, setIsHovering] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const longPressTimer = useRef<ReturnType<typeof setTimeout>>();
    const touchStartTime = useRef<number>(0);
    const [isDragging, setIsDragging] = useState(false);
    const [isMouseDown, setIsMouseDown] = useState(false);
    const navigate = useNavigate();

    // Use cached thumbnail hook
    const { thumbnailSrc, isLoading, handleLoad, handleError } =
      useCachedThumbnail({
        fileId: _id,
        thumbnailUrl: thumbUrl,
        fallbackUrl: DefaultThumbnail,
      });

    const dispatch = useDispatch();

    // Check if the file type is Import
    const isImportedFile = type === 'Import';

    // Get URL from cache if not provided by backend
    const importUrl =
      url || (isImportedFile ? getCachedImportUrl(_id) : undefined);

    // Combine Redux selectors for better performance
    const { parentFolder, isMoving } = useSelector(
      (state: RootState) => ({
        parentFolder: state.library.folders.data.find(
          (folder: FolderType) => folder._id === parentId,
        ),
        isMoving: state.library.movingFileIds.includes(_id),
      }),
      shallowEqual,
    );

    const { folderId } = useParams<{ folderId: string }>();
    const isInRecentsView = folderId === 'recents';

    const handleTouchStart = useCallback(() => {
      touchStartTime.current = Date.now();
      longPressTimer.current = setTimeout(() => {
        setShowMenu(true);
      }, 500);
    }, []);

    const handleTouchEnd = useCallback(() => {
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
      }
    }, []);

    const handleTouchMove = useCallback(() => {
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
      }
    }, []);

    const handleMoveClick = useCallback(
      (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        dispatch(pushModal({ type: 'move-file-modal', files: [file] }));
      },
      [dispatch, file],
    );

    const handleCheckboxClick = useCallback(
      (e: React.MouseEvent) => {
        if (onSelect) {
          e.preventDefault();
          e.stopPropagation();
          onSelect(file);
        }
      },
      [onSelect, file],
    );

    const handleCheckboxChange = useCallback(() => {
      if (onSelect) {
        onSelect(file);
      }
    }, [onSelect, file]);

    // Drag and drop handlers for adding files to chat
    const handleDragStart = (e: React.DragEvent) => {
      // Store the file data as JSON
      e.dataTransfer.setData('application/json', JSON.stringify(file));
      e.dataTransfer.effectAllowed = 'all';
      setIsDragging(true);

      // Set cursor style on document body during drag
      document.body.style.cursor = 'grabbing';

      // Create a simple, small drag preview with thumbnail
      const dragPreview = document.createElement('div');
      dragPreview.className =
        'bg-background border border-border rounded-lg shadow-lg p-2 flex items-center gap-2 max-w-[220px]';
      dragPreview.style.cursor = 'grabbing';

      // Create thumbnail element
      const thumbImg = document.createElement('img');
      thumbImg.src = thumbnailSrc;
      thumbImg.className = 'w-8 h-8 rounded object-cover flex-shrink-0';
      if (thumbnailSrc === DefaultThumbnail) {
        thumbImg.className += ' dark:invert';
      }

      // Create text container
      const textSpan = document.createElement('span');
      textSpan.className = 'text-sm font-medium truncate';
      textSpan.textContent = name;

      dragPreview.appendChild(thumbImg);
      dragPreview.appendChild(textSpan);

      dragPreview.style.position = 'absolute';
      dragPreview.style.top = '-1000px';
      document.body.appendChild(dragPreview);
      e.dataTransfer.setDragImage(dragPreview, 0, 0);
      setTimeout(() => document.body.removeChild(dragPreview), 0);
    };

    const handleDragEnd = () => {
      setIsDragging(false);
      setIsMouseDown(false);
      // Reset cursor on body
      document.body.style.cursor = '';
    };

    const handleMouseDown = () => {
      if (!selectMode) {
        setIsMouseDown(true);
      }
    };

    const handleMouseUp = () => {
      setIsMouseDown(false);
    };

    // Prevent default drag behavior on images and links
    const preventDefaultDrag = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    // Memoized mouse event handlers
    const handleMouseEnter = useCallback(() => {
      if (!isMobile) {
        setShowMenu(true);
        setIsHovering(true);
        // Preload the SelectedVideo component on hover for smooth animation
        preloadSelectedVideo();
      }
    }, [isMobile]);

    const handleMouseLeave = useCallback(() => {
      if (!isMobile) {
        if (!isDropdownOpen) setShowMenu(false);
        setIsHovering(false);
      }
    }, [isMobile, isDropdownOpen]);

    // Handle row click - navigate to video when not clicking checkbox
    const handleRowClick = useCallback(
      (e: React.MouseEvent) => {
        // Don't navigate if clicking on checkbox, action menu, or folder button
        const target = e.target as HTMLElement;
        if (
          target.closest('[role="button"]') ||
          target.closest('button') ||
          target.closest('[data-radix-dropdown-menu-trigger]') ||
          target.closest('[data-radix-dropdown-menu-content]') ||
          target.closest('[role="menuitem"]') ||
          selectMode
        ) {
          return;
        }

        // Pre-load the cached file data into selectedVideo state before navigation
        dispatch(selectVideoFromLibrary(file));

        // Navigate to the file URL
        navigate(fileUrl);
      },
      [dispatch, file, fileUrl, navigate, selectMode],
    );

    if (isImportedFile) {
      return <ProcessingListItem name={name} url={importUrl} />;
    }

    // Show checkbox when hovering OR when in select mode
    const shouldShowCheckbox = isHovering || selectMode;

    // Determine cursor style
    let cursorStyle = 'cursor-grab';
    if (isDragging) {
      cursorStyle = 'opacity-50 cursor-grabbing';
    } else if (isMouseDown) {
      cursorStyle = 'cursor-grabbing';
    } else if (shouldShowCheckbox) {
      cursorStyle = 'cursor-pointer';
    }

    // Vercel-inspired minimal layout with date in separate column
    return (
      <MotionTableRow
        layout
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{
          layout: {
            duration: 0.15,
            ease: [0.25, 0.1, 0.25, 1],
          },
          opacity: { duration: 0.1, ease: 'easeOut' },
          scale: { duration: 0.1, ease: 'easeOut' },
        }}
        data-testid="video-list-tile"
        draggable={!selectMode}
        onDragStart={handleDragStart as any}
        onDragEnd={handleDragEnd as any}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchMove={handleTouchMove}
        onClick={handleRowClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={`
        group relative transition-all duration-200
        ${isSelected && shouldShowCheckbox ? 'bg-accent/10' : ''}
        ${showMenu && !shouldShowCheckbox ? 'bg-muted/50' : 'hover:bg-muted/50'}
        border-b border-border/50
        ${cursorStyle}
      `}
        data-state={isSelected ? 'selected' : undefined}
        style={{
          transformOrigin: 'center',
        }}
      >
        {/* Name Column with metadata underneath */}
        <TableCell className="py-4 px-4">
          <div className="flex items-center gap-3 min-w-0">
            {/* Thumbnail with overlay checkbox */}
            <div className="relative flex-shrink-0">
              {isLoading && thumbnailSrc !== thumbUrl && (
                <Skeleton className="h-9 w-9 rounded absolute inset-0 bg-white dark:bg-black" />
              )}
              <img
                key={_id}
                src={thumbnailSrc}
                alt=""
                loading="eager"
                decoding="async"
                draggable={false}
                onDragStart={preventDefaultDrag}
                className={`h-9 w-9 rounded object-cover ${
                  thumbnailSrc === DefaultThumbnail ? 'dark:invert' : ''
                } ${isLoading && thumbnailSrc !== thumbUrl ? 'opacity-0' : 'opacity-100'}`}
                onLoad={handleLoad}
                onError={handleError}
              />
              {/* Checkbox overlay - only shown on hover or in select mode */}
              {shouldShowCheckbox && (
                <div
                  className="absolute inset-0 flex items-center justify-center bg-background/90 backdrop-blur-[2px] rounded transition-all duration-200"
                  onClick={handleCheckboxClick}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      handleCheckboxClick(e as any);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={handleCheckboxChange}
                    className="h-4 w-4 transition-transform hover:scale-110"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              )}
            </div>

            {/* Title and Metadata */}
            <div className="flex flex-col gap-1.5 min-w-0 flex-1">
              {/* Title */}
              <span
                className="text-sm font-medium truncate leading-snug"
                title={name}
              >
                {name}
              </span>

              {/* Metadata row */}
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground leading-none">
                {/* Duration */}
                {duration && (
                  <span className="flex-shrink-0">
                    {formatDuration(duration)}
                  </span>
                )}

                {/* Separator */}
                {duration && ownerName && (
                  <span className="flex-shrink-0">·</span>
                )}

                {/* Creator */}
                {ownerName && (
                  <span
                    className="truncate flex-shrink-0 max-w-[140px]"
                    title={ownerName}
                  >
                    {ownerName}
                  </span>
                )}

                {/* Folder (Recents only) */}
                {isInRecentsView && (
                  <>
                    {(duration || ownerName) && (
                      <span className="flex-shrink-0">·</span>
                    )}
                    <FolderSelector
                      hasFolder={parentId !== null}
                      folderName={parentFolder?.name}
                      onClick={handleMoveClick}
                      className="text-xs text-muted-foreground"
                    />
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Loading overlay when file is being moved */}
          {isMoving && (
            <div className="absolute inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-10">
              <Loader className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}
        </TableCell>

        {/* Date Column */}
        <TableCell className="py-4 px-4 w-32">
          <span className="text-sm text-muted-foreground whitespace-nowrap">
            {new Intl.DateTimeFormat('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            }).format(new Date(createdAt))}
          </span>
        </TableCell>

        {/* Action Menu Column */}
        <TableCell className="py-4 w-10">
          <div
            className={`transition-all duration-200 flex justify-end ${showMenu || isMobile ? 'opacity-100' : 'opacity-0'}`}
          >
            <FileActionMenu file={file} onOpenChange={setIsDropdownOpen} />
          </div>
        </TableCell>
      </MotionTableRow>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison for memo - only re-render if these props change
    return (
      prevProps.file._id === nextProps.file._id &&
      prevProps.file.name === nextProps.file.name &&
      prevProps.file.thumbUrl === nextProps.file.thumbUrl &&
      prevProps.file.type === nextProps.file.type &&
      prevProps.file.parentId === nextProps.file.parentId &&
      prevProps.isSelected === nextProps.isSelected &&
      prevProps.selectMode === nextProps.selectMode &&
      prevProps.fileUrl === nextProps.fileUrl &&
      prevProps.isMobile === nextProps.isMobile
    );
  },
);

export default FileListItem;
